import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import TreatmentAgencyModal from "@/ui/modals/treatmentAgencies/TreatmentAgencyModal";
import {
  useArchiveTreatmentAgency,
  useCreateTreatmentAgency,
  useTreatmentAgencies,
  useUpdateTreatmentAgency,
} from "@/hooks/useTreatmentAgencies";
import { TreatmentAgency } from "@/domain/treatmentAgencies.interface";
import { TreatmentAgencyPayload } from "@/services/treatmentAgencies.service";
import { useAppTheme } from "@/utils/theme";
import { usePageStore } from "@/hooks/usePageStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import useDebounce from "@/hooks/useDebounce";
import { useUserStore } from "@/hooks/useUserStore";
import { toTitleCase } from "@/utils/toTitleCase";
import { ErrorResponse } from "@/domain/error.interface";

type Filters = {
  search: string;
  status: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

const DEFAULT_FILTERS: Filters = {
  search: "",
  status: "",
  pageNumber: 0,
  pageSize: 10,
  sortField: "name",
  sortOrder: 1,
};

const TreatmentAgenciesPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const debouncedSearch = useDebounce({ value: filters.search, delay: 500 });

  const [modalState, setModalState] = useState<{
    open: boolean;
    treatmentAgency?: TreatmentAgency;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    treatmentAgency?: TreatmentAgency;
  }>({ open: false });

  const createTreatmentAgency = useCreateTreatmentAgency();
  const updateTreatmentAgency = useUpdateTreatmentAgency();
  const archiveTreatmentAgency = useArchiveTreatmentAgency();

  const {
    treatmentAgencies,
    isLoadingTreatmentAgencies,
    isFetchingTreatmentAgencies,
  } = useTreatmentAgencies({
    search: debouncedSearch,
    status: filters.status,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
  });

  useEffect(() => {
    setPageTitle("Treatment Agencies");
  }, []);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, pageNumber: 0 }));
  }, [debouncedSearch, filters.status]);

  const handleOpenCreate = () => setModalState({ open: true });

  const handleOpenEdit = (treatmentAgency: TreatmentAgency) =>
    setModalState({ open: true, treatmentAgency });

  const handleCloseModal = () => setModalState({ open: false });

  const handleSave = async (data: TreatmentAgencyPayload) => {
    const editing = modalState.treatmentAgency;
    try {
      if (editing) {
        await updateTreatmentAgency.mutateAsync({
          id: editing._id,
          data: { ...data, updatedBy: currentUserId },
        });
        showSnackBar("The treatment agency has been updated.", "success");
      } else {
        await createTreatmentAgency.mutateAsync({
          ...data,
          createdBy: currentUserId,
        });
        showSnackBar("The treatment agency has been created.", "success");
      }
      handleCloseModal();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.treatmentAgency;
    if (!target) return;
    try {
      await archiveTreatmentAgency.mutateAsync(target._id);
      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "280px",
    },
    {
      id: "locations",
      isSortable: false,
      disablePadding: false,
      label: "Locations",
      align: "left",
      width: "180px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "140px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "160px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!treatmentAgencies || treatmentAgencies.list.length === 0) return [];

    return treatmentAgencies.list.map((treatmentAgency, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(treatmentAgency),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, treatmentAgency }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const locationsCount = Array.isArray(treatmentAgency.locations)
        ? treatmentAgency.locations.length
        : 0;

      return createDataRow(index, {
        name: <div>{treatmentAgency.name}</div>,
        locations: (
          <Chip
            size="small"
            color={locationsCount > 0 ? "primary" : "default"}
            label={
              <div className="text-xs">
                {`${locationsCount} location${locationsCount === 1 ? "" : "s"}`}
              </div>
            }
          />
        ),
        status: (
          <Chip
            size="small"
            color={treatmentAgency.status === "ACTIVE" ? "success" : "default"}
            label={
              <div className="text-xs">
                {toTitleCase(treatmentAgency.status)}
              </div>
            }
          />
        ),
        createdAt: (
          <div>
            {treatmentAgency.createdAt
              ? dayjs(treatmentAgency.createdAt).format("DD MMM YYYY")
              : "-"}
          </div>
        ),
        action: <ActionMenu id={treatmentAgency._id} options={actionMenu} />,
      });
    });
  }, [treatmentAgencies, theme]);

  const isModalPending =
    createTreatmentAgency.isPending || updateTreatmentAgency.isPending;

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Treatment Agencies
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "100%",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.surface.light2,
                      px: 2,
                      width: {
                        xs: "100%",
                        md: "60%",
                      },
                    }}
                    className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                  >
                    <InputBase
                      className="flex-1"
                      placeholder="Search treatment agency"
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => {
                        setFilters((f) => ({
                          ...f,
                          search: e.target.value,
                        }));
                      }}
                      value={filters.search}
                    />
                    <SearchIcon className="text-icon-primary" />
                  </Box>

                  <FormControl
                    sx={{
                      width: {
                        xs: "100%",
                        md: "240px",
                      },
                    }}
                    size="small"
                  >
                    <InputLabel id="treatment-agencies-status-filter-label">
                      Status
                    </InputLabel>
                    <Select
                      labelId="treatment-agencies-status-filter-label"
                      label="Status"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value as string,
                        }))
                      }
                      sx={{ height: "56px", borderRadius: "8px" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="INACTIVE">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={treatmentAgencies?.totalCount}
                  isLoading={
                    isLoadingTreatmentAgencies || isFetchingTreatmentAgencies
                  }
                  onChangePage={(event, newPage) => {
                    setFilters((f) => ({ ...f, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((f) => ({
                      ...f,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((f) => ({
                      ...f,
                      pageNumber: 0,
                      sortField: orderBy as string,
                      sortOrder: order === "asc" ? 1 : -1,
                    }));
                  }}
                  messageEmptyData="No treatment agencies to show."
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <TreatmentAgencyModal
          open={modalState.open}
          onClose={handleCloseModal}
          treatmentAgency={modalState.treatmentAgency}
          onSave={handleSave}
          isPending={isModalPending}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveTreatmentAgency.isPending}
        />
      )}
    </>
  );
};

export default TreatmentAgenciesPage;
